import React from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Box,
  Collapse,
  VStack,
  RadioGroup,
  Radio,
  IconButton,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Controller, useFormContext } from "react-hook-form";
import { t } from "i18next";


const CommonSelectAdvancedField: React.FC<CommonSelectAdvancedFieldProps> = ({
  name,
  control,
  label,
  categories = [],
  placeholder = t("common:courseCreate.selectCategory"),
  onChangeCustom = (value: any) => null,
}) => {
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(
    undefined
  );
  React.useEffect(() => {
    if (btnRef.current) {
      setMenuWidth(btnRef.current.offsetWidth);
    }
  }, [btnRef.current]);

  const {
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage =
    typeof error === "object" && error !== null ? error.message : undefined;

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={{ required: `${label} is required` }}
        render={({ field }) => {
          let selectedLabel = placeholder;
          categories?.forEach((cat) => {
            const found = cat?.children?.find(
              (sub) => sub?.value === field?.value
            );
            if (found) selectedLabel = `${cat?.label} / ${found?.label}`;
          });
          const isInvalid = !!error;
          return (
            <Menu isLazy>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    ref={btnRef}
                    width="100%"
                    bg="#FFF"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={
                      isInvalid ? "#E53E3E" : isOpen ? "#1819B4" : "#CDD6DC"
                    }
                    textAlign="left"
                    fontWeight={field.value ? 600 : 400}
                    color="#2F2F34"
                    _focus={{
                      borderColor: isInvalid ? "#E53E3E" : "#1819B4",
                      boxShadow: "none",
                    }}
                    _active={{
                      borderColor: isInvalid ? "#E53E3E" : "#1819B4",
                      boxShadow: "none",
                    }}
                    _hover={{
                      borderColor: isInvalid ? "#E53E3E" : "#1819B4",
                    }}
                    rightIcon={<ChevronDownIcon />}
                    px={4}
                    py={2}
                  >
                    {selectedLabel}
                  </MenuButton>
                  <MenuList
                    width={menuWidth ? `${menuWidth}px` : undefined}
                    minWidth="220px"
                    bg="#FFF"
                    maxH="300px"
                    overflowY="auto"
                    ml={"20px"}
                    boxShadow={"4px 4px 8px 0px #0000000F"}
                    p={0}
                  >
                    {categories?.map((cat) => (
                      <Box key={cat.value}>
                        <MenuItem
                          onClick={() =>
                            setExpanded(
                              expanded === cat?.value ? null : cat?.value
                            )
                          }
                          bg="#FFF"
                          color="#2F2F34"
                          fontWeight={600}
                          display="flex"
                          alignItems="center"
                          px={4}
                          py={2}
                          closeOnSelect={false}
                          position="relative"
                          pr={8}
                          _hover={{
                            bg: "gray.100",
                            borderColor: "#1819B4",
                          }}
                        >
                          <span style={{ paddingRight: 8 }}>{cat?.label}</span>
                          <IconButton
                            aria-label={
                              expanded === cat?.value ? "Collapse" : "Expand"
                            }
                            icon={
                              expanded === cat?.value ? (
                                <ChevronDownIcon />
                              ) : (
                                <ChevronRightIcon />
                              )
                            }
                            size="xs"
                            variant="ghost"
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpanded(
                                expanded === cat?.value ? null : cat?.value
                              );
                            }}
                            tabIndex={-1}
                          />
                        </MenuItem>
                        <Collapse in={expanded === cat.value} animateOpacity>
                          <Box pl={6}>
                            <RadioGroup
                              value={field?.value || ""}
                              onChange={field?.onChange}
                            >
                              <VStack align="stretch" spacing={1}>
                                {cat?.children?.map((sub) => (
                                  <MenuItem
                                    key={sub?.value}
                                    onClick={() => {
                                      field.onChange(sub?.value);
                                      onChangeCustom?.(
                                        `${cat?.value}-${sub?.value}`
                                      );
                                    }}
                                    bg={
                                      field?.value === sub?.value
                                        ? "gray.100"
                                        : "#FFF"
                                    }
                                    color={
                                      field?.value === sub?.value
                                        ? "#1819B4"
                                        : "#2F2F34"
                                    }
                                    fontWeight={
                                      field?.value === sub?.value ? 600 : 400
                                    }
                                    _hover={{
                                      bg: "gray.100",
                                      borderColor: "#1819B4",
                                    }}
                                    px={4}
                                    py={2}
                                    closeOnSelect={false}
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Radio
                                      value={sub?.value}
                                      isChecked={field?.value === sub?.value}
                                      colorScheme="purple"
                                      mr={2}
                                    />
                                    <span>{sub?.label}</span>
                                  </MenuItem>
                                ))}
                              </VStack>
                            </RadioGroup>
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </MenuList>
                </>
              )}
            </Menu>
          );
        }}
      />
      <FormErrorMessage>
        {typeof errorMessage === "string" ? errorMessage : null}
      </FormErrorMessage>
    </FormControl>
  );
};

export default CommonSelectAdvancedField;
